import React, { useRef } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Form, Input, Radio, Button, Icon } from 'antd';
import _ from 'lodash';
import styles from './ScreeningQuestionItem.module.scss';
import message from '../BotConfig/ChatbotMessages';
import placeholder from '../../Placeholders/PlaceholdersMessages';
import BotQuestionTypeSelect from './BotQuestionTypeSelect';

const { Group } = Radio;
const { TextArea } = Input;
const MAX_LEVEL = 1;

function ScreeningQuestionItem({
  intl,
  removeQuestion,
  question,
  readOnly = false,
  questions,
  addQuestion,
  showFollowupQuestions,
  onQuestionFieldChange,
  questionErrors,
  getHelpMessageForQuestion,
  collapseFollowupQuestions,
  onCategoryChange,
  setInputBoxSelected,
  updateCollapseOfQuestions,
}) {
  const isAllParentCollapsed = Object.keys(showFollowupQuestions)
    .filter(key => !key.includes('.'))
    .every(key => showFollowupQuestions[key] === false);
  const yesFollowupQuestion = _.find(questions, { Order: `${question.Order}${'.1'}` });
  const noFollowupQuestion = _.find(questions, { Order: `${question.Order}${'.2'}` });
  const followUpCount = Number(!!yesFollowupQuestion) + Number(!!noFollowupQuestion);
  const questionRef = useRef(null);
  const level = question.Order.split('.').length - 1;

  const hasError = questionErrors && questionErrors[question.Order];
  const validationStatus = hasError ? 'error' : '';
  const helpText = getHelpMessageForQuestion(questionErrors, question);

  const handleQuestionChange = e => {
    onQuestionFieldChange('Question', question.Order, e.target.value);
  };

  const handleRemoveQuestion = () => {
    removeQuestion(question.Order);
  };

  const handleExpectedAnswerChange = e => {
    onQuestionFieldChange('ExpectedAnswer', question.Order, e.target.value);
  };

  const handleAddFollowupQuestion = (parentOrder, order) => {
    if (readOnly) return;
    addQuestion(order);
  };

  const handleToggleFollowup = () => {
    if (!showFollowupQuestions[question.Order]) {
      scrollParentByPixel(250 * followUpCount);
    }
    collapseFollowupQuestions(question.Order, !showFollowupQuestions[question.Order]);
  };

  const scrollContainer = (extra = 0) => {
    if (questionRef.current) {
      const el = questionRef.current;
      const rect = el.getBoundingClientRect();
      const scrollParent = el.closest('.ant-modal-body') || window;
      const parentRect =
        scrollParent === window ? { top: 0, height: window.innerHeight } : scrollParent.getBoundingClientRect();
      const elBottom = rect.bottom;
      const parentTop = parentRect.top;
      const parentHeight = parentRect.height;
      const targetScroll = elBottom - parentTop - parentHeight / 2 + extra;
      setTimeout(() => {
        if (scrollParent === window) {
          window.scrollBy({ top: targetScroll, behavior: 'smooth' });
        } else {
          scrollParent.scrollBy({ top: targetScroll, behavior: 'smooth' });
        }
      }, 10);
    }
  };

  const scrollParentByPixel = pixel => {
    const scrollParent = questionRef.current.closest('.ant-modal-body') || window;
    if (scrollParent === window) {
      window.scrollBy({ top: window.scrollY + pixel, behavior: 'smooth' });
    } else {
      scrollParent.scrollBy({ top: pixel, behavior: 'smooth' });
    }
  };

  const handleAddYesFollowup = () => {
    handleAddFollowupQuestion(question.Order, `${question.Order}${'.1'}`);
    scrollContainer(230);
    collapseFollowupQuestions(question.Order, true);
  };

  const handleAddNoFollowup = () => {
    const questionOrdes = questions.map(q => q.Order);
    const diplicateShowFollowupQuestions = { ...showFollowupQuestions };
    handleAddFollowupQuestion(question.Order, `${question.Order}${'.2'}`);
    diplicateShowFollowupQuestions[question.Order] = true;
    let delta = 230;
    if (questionOrdes.includes(`${question.Order}${'.1'}`)) {
      diplicateShowFollowupQuestions[`${question.Order}${'.1'}`] = false;
      delta = 370;
    }
    updateCollapseOfQuestions(diplicateShowFollowupQuestions);
    scrollContainer(delta);
  };

  const renderFollowupButton = (answerType, followupExists, clickHandler) => {
    if (followupExists || !question.Question?.trim() || level > MAX_LEVEL) return null;

    return (
      <Button
        type="link"
        size="small"
        className={`${styles.addFollowup} ${styles[answerType]}`}
        disabled={readOnly}
        onClick={clickHandler}
      >
        <FormattedMessage {...message.addFollowupQuestionLabel} />
      </Button>
    );
  };
  const disableCloseText = !question.Order.includes('.');
  return (
    <div
      className={styles.botQuestionContainer}
      ref={questionRef}
      style={{ cursor: isAllParentCollapsed ? 'move' : 'default' }}
    >
      <div className={styles.botQuestionWrapper}>
        <div className={styles.questionNumber}>{question.Order}</div>
        <div className={styles.botQuestion}>
          <div className={styles.question} style={{ position: 'relative' }} id={`question-order-${question.Order}`}>
            <Form.Item validateStatus={validationStatus} help={helpText}>
              <TextArea
                autoSize={{ minRows: 1, maxRows: 2 }}
                placeholder={intl.formatMessage({ ...placeholder.typeAQuestionLabel })}
                disabled={readOnly}
                onChange={handleQuestionChange}
                className={styles.questionTextarea}
                value={question.Question}
                maxLength={255}
                onFocus={() => setInputBoxSelected(true)}
                onBlur={() => setInputBoxSelected(false)}
              />
            </Form.Item>
            {!question.Id ? (
              <BotQuestionTypeSelect
                value={question.ExpectedAnswer}
                readOnly={readOnly}
                onSelect={val => onCategoryChange(question.Order, val)}
                parentId={`question-order-${question.Order}`}
                disableCloseText={disableCloseText}
              />
            ) : null}
            {!readOnly && <Icon type="minus-circle" onClick={handleRemoveQuestion} style={{ fontSize: 18 }} />}
          </div>

          <Form.Item>
            <Group value={question.ExpectedAnswer?.toUpperCase()} onChange={handleExpectedAnswerChange}>
              <div className={styles.followupOptions}>
                <div className={styles.optionRow}>
                  <Radio value="YES" disabled={readOnly}>
                    <FormattedMessage {...message.yesResponseLabel} />
                  </Radio>
                  {renderFollowupButton('yes', yesFollowupQuestion, handleAddYesFollowup)}
                </div>

                <div className={styles.optionRow}>
                  <Radio value="NO" disabled={readOnly}>
                    <FormattedMessage {...message.noResponseLabel} />
                  </Radio>
                  {renderFollowupButton('no', noFollowupQuestion, handleAddNoFollowup)}
                </div>
              </div>
            </Group>
          </Form.Item>
        </div>
      </div>

      {followUpCount > 0 && (
        <div className={styles.expandOption}>
          <Icon
            type={showFollowupQuestions[question.Order] ? 'down' : 'right'}
            onClick={handleToggleFollowup}
            className={styles.expandButton}
          />
          <FormattedMessage {...message.followupQuestionsCountLabel} values={{ count: followUpCount }} />
        </div>
      )}
    </div>
  );
}

export default injectIntl(ScreeningQuestionItem);
export { ScreeningQuestionItem as ScreeningQuestionItemWithoutInjectIntl };
